import { createId } from '@paralleldrive/cuid2';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import {
  BiChatContextType,
  ChatContextType,
  isBrandInsightsContext,
  isEverythingContext,
  ChatDataSourcesMap,
  ChatSource,
  ChatSourcesMap,
  isBrandContext,
  isFolderContext,
  isProjectContext,
} from '@trinity/chat';
import type { ChatSessionState, ChatState, SettingsState } from './interfaces';
import type { ChatSessionManagerState } from './interfaces.chat-session-manager';

type GenerateEmptyChatSessionStateData = {
  context?: BrandInsightsPageContext.Item | null;
  contextItems: ChatContextItem[];
  features: {
    hcpProfiles: boolean;
    workspaces: boolean;
    engagementFilter: boolean;
  };
  brandIdentifier: string;
  pendingQuery?: string | null;
  landingCardsVisible: boolean;
};

const AI_GENERATED_NAME_TEMPLATE = '{ai-generated-name}';
const SUBJECT_NAME_TEMPLATE = '{subject-name}';

export function generateIntialKolProfileContext(): BrandInsightsPageContext.KolProfile {
  return { type: ChatContextType.KolProfile, data: { id: null }, metadata: { name: '' } };
}

export function buildDefaultChatContext(contextType: ChatContextType): BrandInsightsPageContext.Item {
  switch (contextType) {
    case ChatContextType.KolProfile:
      return generateIntialKolProfileContext();

    case ChatContextType.Global:
      return { type: ChatContextType.Global };

    default:
      return null;
  }
}

function getFirstAgentContext(contextItems: ChatContextItem[]): BrandInsightsPageContext.Item | null {
  const firstItem = contextItems[0];
  if (!firstItem) {
    return null;
  }

  return {
    type: ChatContextType.Agent,
    data: firstItem,
    metadata: {},
  };
}

function hasGlobalContext(contextItems: ChatContextItem[]): boolean {
  return contextItems.some((item) => item.agent.identifier === ChatContextType.Global);
}

function hasKolProfileContext(contextItems: ChatContextItem[]): boolean {
  return contextItems.some((item) => item.agent.identifier === ChatContextType.KolProfile);
}

function resolveStrictFallbackContext(contextItems: ChatContextItem[]): BrandInsightsPageContext.Item {
  const agentContext = getFirstAgentContext(contextItems);

  if (!agentContext) {
    throw new Error('No valid mapped chat context could be determined');
  }

  return agentContext;
}

export function resolveContextForContextItems(
  context: BrandInsightsPageContext.Item,
  contextItems: ChatContextItem[]
): BrandInsightsPageContext.Item {
  switch (context.type) {
    case ChatContextType.Agent: {
      const matched = contextItems.find((item) => item.identifier === context.data.identifier);
      if (!matched) {
        return resolveStrictFallbackContext(contextItems);
      }

      return {
        ...context,
        // Keep canonical context-item defaults, but preserve runtime overrides
        // threaded via actions (for example an updated subject).
        data: {
          ...matched,
          ...context.data,
        },
      };
    }

    case ChatContextType.Global:
      return hasGlobalContext(contextItems) ? context : resolveStrictFallbackContext(contextItems);

    case ChatContextType.KolProfile:
      return hasKolProfileContext(contextItems) ? context : resolveStrictFallbackContext(contextItems);

    default:
      return context;
  }
}

function resolveInitialChatName(context: BrandInsightsPageContext.Item): string {
  if (context.type !== ChatContextType.Agent) {
    return '';
  }

  const template = context.data.chatNameTemplate;
  if (!template) {
    return '';
  }

  if (template.includes(AI_GENERATED_NAME_TEMPLATE)) {
    return '';
  }

  const subjectName = context.data.subject?.name?.trim();
  return subjectName
    ? template.replaceAll(SUBJECT_NAME_TEMPLATE, subjectName)
    : template.replaceAll(SUBJECT_NAME_TEMPLATE, '').replace(/\s+/g, ' ').trim().replace(/^:\s*/, '');
}

export function generateEmptyChatSessionState({
  context,
  contextItems,
  features,
  brandIdentifier,
  pendingQuery,
  landingCardsVisible,
}: GenerateEmptyChatSessionStateData): ChatSessionState.State {
  if (!context) {
    throw new Error('Context is required to generate empty chat session state');
  }

  const resolvedContext = resolveContextForContextItems(context, contextItems);

  return {
    chatInstance: {
      createdOn: new Date(),
      id: null,
      identifier: createId(),
      name: resolveInitialChatName(resolvedContext),
      currentQueryIdentifier: null,
      context: resolvedContext,
      shared: null,
      bookmarked: false,
      filterOverrides: {},
      filters: null,
      brandIdentifier: brandIdentifier,
    },
    input: '',
    selectedQueryIdentifier: null,
    queries: {},
    filters: generateEmptyChatSessionFiltersState({ context: resolvedContext, features }),
    fileUploads: [],
    pendingFindQuotes: false,
    pendingQuery: pendingQuery ?? null,
    pendingHintPromptIdentifier: null,
    landingCardsVisible,
    pendingAttachments: [],
  };
}

type GenerateEmptyChatSessionFiltersState = {
  context?: BrandInsightsPageContext.Item;
  features: {
    hcpProfiles: boolean;
    workspaces: boolean;
    engagementFilter: boolean;
  };
};

export function generateEmptyChatSessionFiltersState({
  context,
  features,
}: GenerateEmptyChatSessionFiltersState): ChatSessionState.State['filters'] {
  if (!context) {
    return {
      custom: [],
      dataSources: [],
      sources: [],
      minDocumentDate: null,
      tags: [],
      projects: [],
      arbitraryFields: {},
    };
  }

  const kolDataSources = [].map((m) => ({
    id: m,
    name: ChatDataSourcesMap.get(m),
  }));

  const sourceOptions = getChatSourceFilterOptions({ context, features });
  const sources = sourceOptions.filter((source) => source.id !== ChatSource.Surveys);

  const kolEngagement = features.engagementFilter
    ? { id: 'proactive', name: 'Proactive' }
    : { id: 'all', name: 'All (Reactive and Proactive)' };

  switch (context.type) {
    case ChatContextType.KolProfile:
      return {
        custom: [],
        dataSources: kolDataSources,
        sources,
        minDocumentDate: null,
        tags: [],
        engagement: kolEngagement,
        projects: [],
        arbitraryFields: {},
      };

    case ChatContextType.Global:
      return {
        custom: [],
        dataSources: [...(features.hcpProfiles ? kolDataSources : [])].filter(Boolean),
        sources,
        minDocumentDate: null,
        tags: [],
        engagement: kolEngagement,
        projects: [],
        arbitraryFields: {},
      };

    default:
      return {
        custom: [],
        dataSources: [],
        sources,
        minDocumentDate: null,
        tags: [],
        engagement: { id: 'all', name: 'All' },
        projects: [],
        arbitraryFields: {},
      };
  }
}

export function generateEmptyChatSessionManagerState(): ChatSessionManagerState.State {
  return {
    currentChatInstanceIdentifier: null,
    nextChatInstanceIdentifier: null,
    status: 'uninitialized',
  };
}

type GenerateEmptyChatStateData = {
  settings: SettingsState.State;
} & GenerateEmptyChatSessionStateData;

export function generateEmptyChatState(data: GenerateEmptyChatStateData): ChatState.State {
  const context = data.context || data.settings.defaultContext;
  const session = generateEmptyChatSessionState({
    context,
    features: data.features,
    brandIdentifier: data.brandIdentifier,
    contextItems: data.contextItems,
    landingCardsVisible: data.landingCardsVisible,
  });

  return {
    contextItems: data.contextItems,
    settings: data.settings,
    history: {
      manager: {
        mode: 'page',
        cursor: null,
        loading: false,
        hasMore: false,
        initialized: false,
      },
      items: [],
      shared: [],
      tabs: {
        index: 0,
        items: [session.chatInstance.identifier],
      },
    },
    session,
    sessionManager: generateEmptyChatSessionManagerState(),
    unsavedTabSnapshots: {},
  };
}

type SourceOptionsContextType = ChatContextType | 'workspace-record';

function resolveSourceOptionsContextType(
  context: BrandInsightsPageContext.Item | undefined
): SourceOptionsContextType | undefined {
  if (!context) {
    return undefined;
  }

  if (context.type !== ChatContextType.Agent) {
    return context.type;
  }

  if (context.data.identifier === BiChatContextType.Global) {
    return ChatContextType.Global;
  }
  if (context.data.identifier === BiChatContextType.Brand) {
    return ChatContextType.Brand;
  }
  if (context.data.identifier === BiChatContextType.Folder) {
    return ChatContextType.Folder;
  }
  if (context.data.identifier === BiChatContextType.Project) {
    return ChatContextType.Project;
  }
  if (
    context.data.identifier === BiChatContextType.Transcript ||
    context.data.identifier === BiChatContextType.Document
  ) {
    return 'workspace-record';
  }

  return context.type;
}

export function getChatSourceFilterOptions({ context }: GenerateEmptyChatSessionFiltersState) {
  const sources: ChatSource[] = [];
  const sourceContextType = resolveSourceOptionsContextType(context);

  if (sourceContextType === ChatContextType.KolProfile) {
    sources.push(ChatSource.Profiles, ChatSource.MslInteractions);
  }

  const workspaceSources = [
    ChatSource.Presentations,
    ChatSource.Text,
    ChatSource.Transcripts,
    ChatSource.Dashboards,
  ];

  if (sourceContextType === ChatContextType.Brand || isBrandContext(context)) {
    sources.push(...workspaceSources);
  }
  if (sourceContextType === ChatContextType.Folder || isFolderContext(context)) {
    sources.push(...workspaceSources);
  }
  if (
    sourceContextType === ChatContextType.Global ||
    (isEverythingContext(context) && isBrandInsightsContext(context))
  ) {
    sources.push(...workspaceSources);
  }
  if (sourceContextType === 'workspace-record') {
    sources.push(...workspaceSources);
  }

  if (sourceContextType === ChatContextType.Project || isProjectContext(context)) {
    sources.push(...workspaceSources);
    sources.push(ChatSource.SurveyAnalyses);

    // Survey is only exists in InsightsEDGE (brand-insights), not in the GenAI Platform
    if (context?.type === ChatContextType.Agent || (context != null && isBrandInsightsContext(context))) {
      sources.push(ChatSource.Surveys);
    }
  }

  return sources.map((s) => ({
    id: s,
    name: ChatSourcesMap.get(s),
  }));
}
